import type { AdminPermissionValue } from "~~/schemas/admin-role";
import type { AdminUser } from "~~/schemas/admin-user";
import routerOptions from "~/router.options";

export default defineNuxtRouteMiddleware(async (to) => {
  const authStore = useAuthStore();
  const authRequired = to.meta.authRequired !== false;

  if (import.meta.client) {
    const token = localStorage.getItem("token");
    if (token && !authStore.token) {
      authStore.token = token;
    }
  }

  if (authRequired && !authStore.token) {
    return navigateTo("/login");
  }

  // If we have a token but no user (e.g. after F5), hydrate before checking
  // permissions. Otherwise the guard would skip and the page would render
  // with permission checks defaulting to false.
  if (
    import.meta.client &&
    authStore.token &&
    !authStore.user
  ) {
    try {
      const { $api } = useNuxtApp();
      const profile = await $api<AdminUser>("/profile");
      authStore.updateUser(profile);
    } catch {
      authStore.logout();
      return navigateTo("/login");
    }
  }

  const required = to.meta.requiredPermission as
    | AdminPermissionValue
    | AdminPermissionValue[]
    | undefined;

  if (required) {
    if (!authStore.user) {
      return navigateTo("/login");
    }
    const { can } = useAccess();
    if (!can(required)) {
      // "/" is the landing page after login, so an admin without dashboard
      // access lands on the first page their role can open instead of being
      // greeted by "no access". Deep links still get /403.
      if (to.path !== "/") {
        return navigateTo("/403");
      }
      const routes = (routerOptions.routes?.([]) ?? []) as {
        path: string;
        meta?: { layout?: string; requiredPermission?: AdminPermissionValue | AdminPermissionValue[] };
      }[];
      const landing = routes.find((route) => {
        if (route.path === "/" || route.path.includes(":") || route.meta?.layout === "blank") {
          return false;
        }
        const perm = route.meta?.requiredPermission;
        return !perm || can(perm);
      });
      return navigateTo(landing?.path ?? "/profile");
    }
  }
});
